/**
 * Launcher visibility for invited ASI role holders.
 * Reuses loadAsiRoleAssignmentsForUid (same as TemplateLibrary / trainer pages)
 * plus the own-uid staff grant doc.
 */
import { useEffect, useState } from "react";
import { useAuth } from "hooks/useAuth";
import {
  getLauncherRole,
  ROLE_APP_IDS,
} from "components/appLauncher/appCatalog";
import {
  loadAsiRoleAssignmentsForUid,
  loadAsiStaffAssignmentForUid,
} from "../services/asiWorkflowDataService";
import { userHasAsiLauncherGrant } from "../utils/asiLauncherAccess";

export function useAsiLauncherAccess() {
  const { user, userData, isGlobalAdmin, isRegionalAdmin, userLoading } =
    useAuth();
  const [hasAsiAccess, setHasAsiAccess] = useState(false);

  const roleKey = getLauncherRole({ userData, isGlobalAdmin, isRegionalAdmin });
  // Skip the grant read only when this catalog already lists the tile.
  // Institution and regional admins no longer do (owner 2026-10-12).
  const catalogAlreadyHasTile = (ROLE_APP_IDS[roleKey] || []).includes(
    "appliedsuperintelligence",
  );
  const uid = user?.uid || null;

  useEffect(() => {
    if (userLoading) return undefined;
    if (!uid || catalogAlreadyHasTile) {
      setHasAsiAccess(false);
      return undefined;
    }

    let cancelled = false;
    Promise.all([
      loadAsiRoleAssignmentsForUid(uid),
      loadAsiStaffAssignmentForUid(uid),
    ])
      .then(([assignments, staffAssignment]) => {
        if (cancelled) return;
        setHasAsiAccess(
          userHasAsiLauncherGrant({ assignments, staffAssignment }),
        );
      })
      .catch(() => {
        if (!cancelled) setHasAsiAccess(false);
      });

    return () => {
      cancelled = true;
    };
  }, [uid, userLoading, catalogAlreadyHasTile]);

  return { hasAsiAccess };
}

export default useAsiLauncherAccess;
